import type { DragContext } from '@model-framework/ui';
import { DraggableSVGGElement } from '@model-framework/ui';
import type { ViewId, ViewModelId } from 'balus-common';
import type { ViewName } from '@view-model/domain/view';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { useD3DblClickCallback } from '@view-model/models/common/hooks';
import { useCallback, useRef, useState } from 'react';
import { BaseTitleBar } from './BaseTitleBar';
import type { ThemeColor } from '@view-model/models/common/color';
import type { GetGuardProps } from '@model-framework/drag-file/useDragFileGuard';

type Props = {
  viewRect: Rect;
  name: ViewName;
  viewId: ViewId;
  viewModelId: ViewModelId;
  isLocked: boolean;
  hasMeaningPanel: boolean;
  onClick: (e: React.MouseEvent<SVGGElement>) => void;
  onCopyView: () => void;
  onOpenPopupMenu: () => void;
  onDragStart: (viewId: ViewId) => void;
  onDrag: (context: DragContext) => void;
  onDragEnd: () => void;
  onAddMeaningPanel: () => void;
  onToggleMeaningPanel: () => void;
  onToggleShowNodeCreatedUser: () => void;
  width: number;
  height: number;
  margin: number;
  menuContainerWidth: number;
  shouldHideMenuButtons: boolean;
  viewThemeColor: ThemeColor;
  getGuardProps: GetGuardProps;
  shouldSuppressDrag?: (e: MouseEvent) => boolean;
};

export const DraggableTitleBar = ({
  viewRect,
  name,
  viewId,
  viewModelId,
  isLocked,
  hasMeaningPanel,
  onClick,
  onCopyView,
  onOpenPopupMenu,
  onDragStart,
  onDrag,
  onDragEnd,
  onAddMeaningPanel,
  onToggleMeaningPanel,
  onToggleShowNodeCreatedUser,
  width,
  height,
  margin,
  menuContainerWidth,
  shouldHideMenuButtons,
  viewThemeColor,
  getGuardProps,
  shouldSuppressDrag,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const ref = useRef<SVGRectElement | null>(null);

  const startEdit = useCallback(() => setIsEditing(true), []);
  useD3DblClickCallback(ref, startEdit, !isLocked);

  const handleOnNameEndEdit = useCallback(() => setIsEditing(false), []);
  const handleDragStart = useCallback(() => onDragStart(viewId), [onDragStart, viewId]);

  return (
    <DraggableSVGGElement
      position={viewRect.topLeft()}
      onDragStart={handleDragStart}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
      onClick={onClick}
      shouldSuppressDrag={shouldSuppressDrag}
    >
      <BaseTitleBar
        ref={ref}
        width={width}
        height={height}
        margin={margin}
        menuContainerWidth={menuContainerWidth}
        shouldHideMenuButtons={shouldHideMenuButtons}
        viewThemeColor={viewThemeColor}
        name={name}
        viewId={viewId}
        viewModelId={viewModelId}
        isLocked={isLocked}
        isEditing={isEditing}
        hasMeaningPanel={hasMeaningPanel}
        onNameEndEdit={handleOnNameEndEdit}
        onCopyView={onCopyView}
        onOpenPopupMenu={onOpenPopupMenu}
        onAddMeaningPanel={onAddMeaningPanel}
        onToggleMeaningPanel={onToggleMeaningPanel}
        onToggleShowNodeCreatedUser={onToggleShowNodeCreatedUser}
        getGuardProps={getGuardProps}
      />
    </DraggableSVGGElement>
  );
};
